import { z } from 'zod';
import {
  DTWarranty,
  FBD_Consumer_Public,
  FBD_Warranty,
  FBD_WarrantyHistory,
  DTWarranty_History,
  DTWarrantyHistory_Snapshot,
  Z_ConsumerInfo,
  PersonaTypeSingleLetter,
  PersonaIdTypeSplitter,
} from '@rabbit/data/types';
import { DR_WarrantyHistory, DR_Warranty } from '@rabbit/data/portal';
import { useFirestoreQueryData } from '@react-query-firebase/firestore';
import { query, where } from 'firebase/firestore';

import { useEffect, useState } from 'react';

export interface WarrantyHistoryShape {
  warrantyHistory: DTWarranty[] | null;
  consumer: z.infer<typeof Z_ConsumerInfo> | null;
  warrantyHistoryLoading: boolean;
}

export function useGetWarrantyHistory(
  warrantyLink: string,
  tenantLink: string
) {
  const [warrantyDetails, setWarrantyData] =
    useState<WarrantyHistoryShape | null>(null);

  const warrantyHistoryQuery = warrantyLink
    ? query(
        DR_WarrantyHistory.collection(),
        where('docid', '==', warrantyLink),
        where(
          'warrantorLink',
          '==',
          PersonaTypeSingleLetter.Warrantor + PersonaIdTypeSplitter + tenantLink
        )
      )
    : undefined;

  const warrantyHistoryResult = useFirestoreQueryData(
    ['warranty-history', warrantyLink],
    warrantyHistoryQuery,
    {
      subscribe: true,
    },
    {
      enabled: !!warrantyLink,
    }
  );

  const warrantyQuery = warrantyLink
    ? query(
        DR_Warranty.collection(),
        where('docid', '==', warrantyLink),
        where(
          'warrantorLink',
          '==',
          PersonaTypeSingleLetter.Warrantor + PersonaIdTypeSplitter + tenantLink
        )
      )
    : undefined;

  const warrantyResult = useFirestoreQueryData(
    ['warranty', warrantyLink],
    warrantyQuery,
    {
      subscribe: true,
    },
    {
      enabled: !!warrantyLink,
    }
  );

  const getData = async (): Promise<WarrantyHistoryShape> => {
    if (!warrantyLink) {
      return {
        warrantyHistory: null,
        consumer: null,
        warrantyHistoryLoading: false,
      };
    }

    const warranty = warrantyResult?.data?.[0] || null;
    if (!warranty) {
      throw new Error('Warranty not found');
    }

    const warrantyHistoryData: DTWarranty_History | null =
      warrantyHistoryResult?.data?.[0] || null;
    if (!warrantyHistoryData) {
      throw new Error('Warranty not Found');
    }

    const warrantyHistorySnapshot =
      warrantyHistoryData.warrantySnapshot as DTWarrantyHistory_Snapshot[];
    if (!warrantyHistorySnapshot) {
      throw new Error(
        `Warranty history not found for warranty: ${warrantyLink}`
      );
    }

    const warrantyHistory = warrantyHistorySnapshot.map(
      (snapshot) => snapshot.content
    );

    // fetch consumer data of warranty
    const consumerInfo = await FBD_Consumer_Public.get(warranty.consumerLink);
    if (!consumerInfo) {
      throw new Error('Warranty customer data not found');
    }

    let consumer: z.infer<typeof Z_ConsumerInfo> = {
      firstName: consumerInfo.splitname?.first ?? '',
      lastName: consumerInfo.splitname?.last ?? '',
      consumerEmail: consumerInfo.email ?? '',
      phoneNumber: consumerInfo.phone ?? '',
    };
    if (consumerInfo.address) {
      consumer = {
        ...consumer,
        address: consumerInfo.address[consumerInfo.address.length - 1],
      };
    }
    return {
      warrantyHistory,
      consumer,
      warrantyHistoryLoading:
        warrantyHistoryResult.isLoading || warrantyResult.isLoading,
    };
  };

  useEffect(() => {
    if (!warrantyLink || warrantyResult.isLoading) {
      return;
    }

    (async () => {
      const data = await getData();
      setWarrantyData(data);
    })().catch((err) => console.error(err));
  }, [warrantyLink, warrantyResult.isLoading]);

  return warrantyDetails;
}
